import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  SelectChangeEvent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop
} from '@mui/material';
import {
  Home as HomeIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';
import { useUserPermissions } from '../../../hooks/useUserPermissions';
import { API_URL } from '../../../utils/config';

export const AddVendorCustomerPage: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { hasPermission } = useUserPermissions();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [vendorName, setVendorName] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isDuplicateNameError, setIsDuplicateNameError] = useState(false);

  // Form state - Identity
  const [displayName, setDisplayName] = useState<string>('');
  const [givenName, setGivenName] = useState<string>('');
  const [middleName, setMiddleName] = useState<string>('');
  const [familyName, setFamilyName] = useState<string>('');
  const [suffix, setSuffix] = useState<string>('');

  // Form state - Contact
  const [primaryPhone, setPrimaryPhone] = useState<string>('');
  const [mobilePhone, setMobilePhone] = useState<string>('');
  const [primaryEmail, setPrimaryEmail] = useState<string>('');
  const [website, setWebsite] = useState<string>('');

  // Form state - Billing Address
  const [billAddrLine1, setBillAddrLine1] = useState<string>('');
  const [billAddrLine2, setBillAddrLine2] = useState<string>('');
  const [billAddrCity, setBillAddrCity] = useState<string>('');
  const [billAddrCountry, setBillAddrCountry] = useState<string>('');
  const [billAddrCountrySubDivisionCode, setBillAddrCountrySubDivisionCode] = useState<string>('');
  const [billAddrPostalCode, setBillAddrPostalCode] = useState<string>('');

  // Form state - Currency
  const [currency, setCurrency] = useState<string>('USD');

  const currencies = ['USD', 'CAD', 'EUR', 'GBP', 'AUD', 'JPY', 'CNY', 'INR'];

  useEffect(() => {
    const fetchVendorDetails = async () => {
      if (!vendorId) return;

      setLoading(true);
      setError(null);
      try {
        const response = await authenticatedFetch(
          `${API_URL}/api/accounting/admin/vendors/${vendorId}`,
          {
            timeout: 30000 // 30 seconds timeout
          }
        );

        if (!response || !response.ok) {
          if (response?.status === 403) {
            throw new Error('Access denied. You do not have permission to view this vendor.');
          }
          if (response?.status === 404) {
            throw new Error('Vendor not found');
          }
          throw new Error('Failed to fetch vendor details');
        }

        const result = await response.json();
        setVendorName(result.data.displayName);
      } catch (err: any) {
        console.error('Error fetching vendor details:', err);
        setError(err.message || 'Failed to fetch vendor details');
      } finally {
        setLoading(false);
      }
    };

    fetchVendorDetails();
  }, [vendorId]);

  const handleCancel = () => {
    navigate(`/admin/hub/accounting/vendors/${vendorId}`);
  };

  const clearForm = () => {
    setDisplayName('');
    setGivenName('');
    setMiddleName('');
    setFamilyName('');
    setSuffix('');
    setPrimaryPhone('');
    setMobilePhone('');
    setPrimaryEmail('');
    setWebsite('');
    setBillAddrLine1('');
    setBillAddrLine2('');
    setBillAddrCity('');
    setBillAddrCountry('');
    setBillAddrCountrySubDivisionCode('');
    setBillAddrPostalCode('');
    setCurrency('USD');
  };

  const validateForm = (): string | null => {
    if (!displayName.trim()) return 'Please enter a display name';

    // Email validation
    if (primaryEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(primaryEmail)) {
        return 'Please enter a valid email address';
      }
    }

    if (!billAddrLine1.trim()) return 'Please enter billing address line 1';
    if (!billAddrCity.trim()) return 'Please enter billing city';
    if (!billAddrCountry.trim()) return 'Please enter billing country';
    if (!billAddrCountrySubDivisionCode.trim()) return 'Please enter billing state/province';
    if (!billAddrPostalCode.trim()) return 'Please enter billing postal code';
    if (!currency) return 'Please select a currency';
    return null;
  };

  const handleSubmit = async () => {
    // Clear previous errors
    setValidationError(null);
    setSubmitError(null);
    setIsDuplicateNameError(false);

    // Validate form
    const validationErr = validateForm();
    if (validationErr) {
      setValidationError(validationErr);
      return;
    }

    setSubmitting(true);

    try {
      const customerData = {
        displayName,
        givenName: givenName || undefined,
        middleName: middleName || undefined,
        familyName: familyName || undefined,
        suffix: suffix || undefined,
        companyName: displayName,
        contact: {
          primaryPhone: primaryPhone || undefined,
          mobilePhone: mobilePhone || undefined,
          primaryEmail: primaryEmail || undefined,
          website: website || undefined
        },
        billingAddress: {
          line1: billAddrLine1,
          line2: billAddrLine2 || undefined,
          city: billAddrCity,
          country: billAddrCountry,
          countrySubDivisionCode: billAddrCountrySubDivisionCode,
          postalCode: billAddrPostalCode
        },
        currencyCode: currency
      };

      const response = await authenticatedFetch(
        `${API_URL}/api/accounting/admin/vendors/${vendorId}/customers`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(customerData)
        }
      );

      if (!response || !response.ok) {
        const result = await response?.json();
        // Check if it's a duplicate name error (409 status)
        if (response?.status === 409) {
          setIsDuplicateNameError(true);
        }
        // Use message field if available (for detailed error messages), otherwise use error field
        const errorMessage = result?.message || result?.error || 'Failed to create customer';
        throw new Error(errorMessage);
      }

      // Success - show modal and clear form
      setShowSuccessModal(true);
      clearForm();
    } catch (err: any) {
      console.error('Error creating customer:', err);
      setSubmitError(err.message || 'Failed to create customer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Link
          color="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate(`/admin/hub/accounting/vendors/${vendorId}`)}
        >
          {vendorName}
        </Link>
        <Typography color="text.primary">Add New Customer</Typography>
      </Breadcrumbs>

      {/* Page Title */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <PersonAddIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Add New Customer
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary" sx={{ ml: 7 }}>
          {vendorName}
        </Typography>
      </Box>

      {/* Form */}
      <Paper sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Identity Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Customer Identity
            </Typography>
          </Grid>

          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              required
              label="Company Name"
              value={displayName}
              onChange={(e) => {
                setDisplayName(e.target.value);
                if (isDuplicateNameError) {
                  setIsDuplicateNameError(false);
                }
              }}
              error={isDuplicateNameError}
              helperText="Required: The name displayed for this customer"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth required>
              <InputLabel id="currency-select-label">Customer Invoice Currency</InputLabel>
              <Select
                labelId="currency-select-label"
                id="currency-select"
                value={currency}
                label="Customer Invoice Currency"
                onChange={(e: SelectChangeEvent) => setCurrency(e.target.value)}
              >
                {currencies.map((curr) => (
                  <MenuItem key={curr} value={curr}>
                    {curr}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Contact Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Primary Contact / Accounts Payable Contact
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Given Name"
              value={givenName}
              onChange={(e) => setGivenName(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={1.5}>
            <TextField
              fullWidth
              label="MI"
              value={middleName}
              onChange={(e) => setMiddleName(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Family Name"
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={2.5}>
            <TextField
              fullWidth
              label="Suffix"
              value={suffix}
              onChange={(e) => setSuffix(e.target.value)}
              placeholder="Jr., Sr."
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Primary Phone"
              value={primaryPhone}
              onChange={(e) => setPrimaryPhone(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Mobile Phone"
              value={mobilePhone}
              onChange={(e) => setMobilePhone(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="email"
              label="Primary Email"
              value={primaryEmail}
              onChange={(e) => setPrimaryEmail(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="url"
              label="Website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://example.com"
            />
          </Grid>

          {/* Billing Address Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Billing Address
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Address Line 1"
              value={billAddrLine1}
              onChange={(e) => setBillAddrLine1(e.target.value)}
              helperText="Required: Street address"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address Line 2"
              value={billAddrLine2}
              onChange={(e) => setBillAddrLine2(e.target.value)}
              placeholder="Apt, Suite, Unit, Building"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              required
              label="City"
              value={billAddrCity}
              onChange={(e) => setBillAddrCity(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              required
              label="State/Province"
              value={billAddrCountrySubDivisionCode}
              onChange={(e) => setBillAddrCountrySubDivisionCode(e.target.value)}
              helperText="E.g., CA, Ontario, NY"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              required
              label="Postal Code"
              value={billAddrPostalCode}
              onChange={(e) => setBillAddrPostalCode(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Country"
              value={billAddrCountry}
              onChange={(e) => setBillAddrCountry(e.target.value)}
              helperText="E.g., USA, Canada, United Kingdom"
            />
          </Grid>

          {/* Error Messages */}
          {validationError && (
            <Grid item xs={12}>
              <Alert severity="error">{validationError}</Alert>
            </Grid>
          )}
          {submitError && (
            <Grid item xs={12}>
              <Alert severity="error">{submitError}</Alert>
            </Grid>
          )}

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? 'Creating...' : 'Create Customer'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onClose={handleCloseSuccessModal}>
        <DialogTitle>Success</DialogTitle>
        <DialogContent>
          <Typography>
            Customer added successfully to QuickBooks and associated with {vendorName}!
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSuccessModal} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Creating Customer Overlay */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          position: 'fixed',
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
        open={submitting}
      >
        <CircularProgress color="inherit" size={60} />
        <Typography variant="h6">Creating customer...</Typography>
        <Typography variant="body2">Please wait while we create the customer</Typography>
      </Backdrop>
    </Container>
  );
};